/**
 * 원시 색상 팔레트 — 무채색·상태색은 토스(TDS) 문법 (2026-07-31, reference/design 스크린샷 기준),
 * 브랜드 액센트(코발트·연두)는 2026-07-30 확정값 유지.
 *
 * 컴포넌트는 이 값을 직접 쓰지 않는다 — 반드시 theme의 의미 토큰(semantic)을 경유한다.
 * 그래야 라이트/다크 전환과 리브랜딩이 한 곳에서 끝난다.
 */
export const palette = {
  /** TDS 그레이 — 토스의 무채색 축. 950은 다크 모드 바닥(토스 다크 근사) */
  neutral: {
    50: '#f9fafb',
    100: '#f2f4f6',
    200: '#e5e8eb',
    300: '#d1d6db',
    400: '#b0b8c1',
    500: '#8b95a1',
    600: '#6b7684',
    700: '#4e5968',
    800: '#333d4b',
    900: '#191f28',
    950: '#101013',
  },
  /** 브랜드 액센트 — 코발트 (hue 228, 2026-07-30 확정 유지) */
  accent: {
    50: '#f0f3fe',
    100: '#dde4fd',
    200: '#b8c5fa',
    300: '#899ff5',
    400: '#5f7df1',
    500: '#3559e9',
    600: '#1a40d5',
    700: '#1836af',
    800: '#182f8b',
    900: '#192a6b',
    950: '#121b3f',
  },
  /** 식집사 전용 액센트 — 연두 (hue 105, 유지). 서비스별 액센트는 theme.ts의 accents 참조 */
  leaf: {
    50: '#f4fef0',
    100: '#e5fddd',
    200: '#c8fab8',
    300: '#a4f589',
    400: '#84f15f',
    500: '#62e935',
    600: '#49d51a',
    700: '#3eaf18',
    800: '#358b18',
    900: '#2e6b19',
    950: '#1e3f12',
  },
  /** 범용 램프 — 아직 서비스 액센트로 연결되지 않은 여유 색. 필요해지면 accents에 세트를 추가해 끼운다 */
  /** 앰버 (hue 32) — 따뜻한 라이브/공연 느낌. soundtether 후보 방향 */
  amber: {
    50: '#fef7ed',
    100: '#fdecd3',
    200: '#fbd6a3',
    300: '#f9bd6e',
    400: '#f7a343',
    500: '#f48a1f',
    600: '#d96f0e',
    700: '#b3560c',
    800: '#8f4511',
    900: '#6b3310',
    950: '#3d1a07',
  },
  /** 바이올렛 (hue 265) — 오디오/파형 톤. 코발트(228)·연두(105)와 거리가 멀어 충돌 적음 */
  violet: {
    50: '#f4f2fe',
    100: '#e9e4fd',
    200: '#d4c9fb',
    300: '#b9a3f7',
    400: '#9b7ff0',
    500: '#7d5ce6',
    600: '#6a40d0',
    700: '#5a35ab',
    800: '#4a2d87',
    900: '#3d2768',
    950: '#251740',
  },
  /**
   * 로즈 (hue 348) — 예식·축하 톤 (첫장면). danger(레드)와 헷갈리지 않게 채도를 낮추고
   * 자주 쪽으로 밀었다. 라이트 액센트는 700 이라야 흰 배경에서 글자로 써도 대비가 넉넉하다(7.2:1).
   */
  rose: {
    50: '#fdf2f4',
    100: '#fbe4e8',
    200: '#f6c8d1',
    300: '#eda3b3',
    400: '#e07d94',
    500: '#cf5b78',
    600: '#b74260',
    700: '#98354f',
    800: '#7a2d41',
    900: '#5f2634',
    950: '#38141d',
  },
  /** 틸 (hue 175) — 청량·전파 느낌. 코발트와 명도만 다른 같은 계열이라 묶일 수 있음 */
  teal: {
    50: '#effcf9',
    100: '#d3f7f1',
    200: '#a9efe7',
    300: '#73e1d6',
    400: '#44cfbf',
    500: '#1bb5a3',
    600: '#0c9483',
    700: '#0a756b',
    800: '#0c5d55',
    900: '#0c4a44',
    950: '#042f2b',
  },
  success: {
    400: '#2bd28f',
    500: '#00c471',
    600: '#009463',
    900: '#0a3d29',
  },
  warning: {
    400: '#ffc342',
    500: '#ffb331',
    600: '#cf8a00',
    900: '#46340c',
  },
  /**
   * 토스 레드 (#f04452 = 500) 기준 램프. **라이트 테마의 `danger` 는 600 이다** —
   * 500 은 흰 바탕에서 3.71:1 이라 글자로도 면으로도 대비가 모자란다 (theme.ts 참고)
   */
  danger: {
    400: '#f66570',
    500: '#f04452',
    600: '#d22030',
    900: '#451920',
  },
  white: '#ffffff',
  black: '#000000',
} as const

export type Palette = typeof palette
